/*  React  */
import {
    Button,
    Flex,
    IconWithVariant,
    ProgressCircle
} from '@auviknetworks/iceberg-ui';
import { Skeleton, Typography, useTheme } from '@mui/material';
import { usePanelContext } from 'application/Shared/Components/Dashboard/Panels/PanelContext';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, type To } from 'react-router';

interface Step {
    action: {
        label: string;
        href: To;
    };
    isCompleted: boolean;
    title: string;
}

const Steps = ({ steps }: { steps: Step[] }) => {
    const { t } = useTranslation();

    return (
        <Flex
            flexDirection="column"
            sx={{ gap: '24px', width: '100%' }}
            flexGrow={1}
        >
            {steps.map(({ action, isCompleted, title }) => (
                <Flex
                    key={title}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ gap: '8px' }}
                >
                    <Flex alignItems="center" sx={{ gap: '8px' }}>
                        <IconWithVariant
                            size={isCompleted ? 16 : 14}
                            variant={isCompleted ? 'secondary' : 'disabled'}
                            src={isCompleted ? 'CheckFilled' : 'CircleFilled'}
                        />
                        <Typography component="span">{t(title)}</Typography>
                    </Flex>
                    {action && !isCompleted && (
                        <Link
                            to={action.href}
                            data-trackid={`customPanelShortcutLink_${title}`}
                        >
                            <Button
                                data-trackid={`customPanelShortcutBtn_${title}`}
                                variant="secondary"
                                size="small"
                            >
                                {t(action.label)}
                            </Button>
                        </Link>
                    )}
                </Flex>
            ))}
        </Flex>
    );
};

const NormalShortcuts = () => {
    const shortcuts = [
        {
            action: { href: './reports', label: 'Common.run' },
            isCompleted: false,
            title: 'deploymentGuide.optimization.AutomatedReports.name'
        },
        {
            action: {
                href: './alerts/configurations',
                label: 'Common.configure'
            },
            isCompleted: false,
            title: 'deploymentGuide.optimization.setupAlerts.name'
        },
        {
            action: {
                href: './saasOps/onboard',
                label: 'Common.onboard'
            },
            isCompleted: false,
            title: 'deploymentGuide.optimization.onboardEmployee.name'
        },
        {
            action: { href: './reports/offboard', label: 'Common.run' },
            isCompleted: false,
            title: 'deploymentGuide.optimization.OffboardWorkflow.name'
        },
        {
            action: {
                href: './breaches',
                label: 'Common.review'
            },
            isCompleted: false,
            title: 'deploymentGuide.optimization.reviewVendorIncidents.name'
        },
        {
            action: {
                href: './tags',
                label: 'Common.create'
            },
            isCompleted: false,
            title: 'deploymentGuide.optimization.createCustomTags.name'
        },
        {
            action: {
                href: './bulk?field=business_owner',
                label: 'Common.assign'
            },
            isCompleted: false,
            title: 'deploymentGuide.management.BusinessOwner.name'
        }
    ];

    return <Steps steps={shortcuts} />;
};

const OnboardingShortcuts = ({
    skipOnboarding
}: {
    skipOnboarding: () => void;
}) => {
    const { palette } = useTheme();
    const { t } = useTranslation();

    const [progress, setProgress] = useState(0);

    const {
        queryResults: { data = {} }
    } = usePanelContext();

    const { agentDeployed, idpIntegration, saasOpsIntegration } = data;

    const steps: Step[] = [
        {
            action: {
                href: './identityManagement/deploymentScripts',
                label: 'Common.deploy'
            },
            isCompleted: agentDeployed?.completed,
            title: 'onboarding.agent'
        },
        {
            action: {
                href: './identityManagement/configuration',
                label: 'Common.setup'
            },
            isCompleted: idpIntegration?.completed,
            title: 'onboarding.identityManagement'
        },
        {
            action: {
                href: './saasOpsIntegrations',
                label: 'Common.configure'
            },
            isCompleted: saasOpsIntegration?.completed,
            title: 'onboarding.SaasOps'
        }
    ];

    // Calculate the progress based on the completed steps
    useEffect(() => {
        setProgress(
            Math.floor(
                steps.reduce(
                    (acc, step) =>
                        acc + (step.isCompleted ? 100 / steps.length : 0),
                    0
                )
            )
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agentDeployed, idpIntegration, saasOpsIntegration]);

    return (
        <Flex sx={{ gap: '24px' }} flexDirection="column" flexGrow={1}>
            <Flex
                sx={{ gap: '24px' }}
                alignItems="center"
                flexDirection="column"
            >
                <ProgressCircle
                    data={[
                        {
                            fill: palette.chart.success,
                            name: 'Progress',
                            value: progress
                        }
                    ]}
                    emptyMessage={t('Common.noData.short')}
                    text={`${progress}%`}
                    title={progress === 100 ? t('Common.completed') : undefined}
                    responsiveContainerHeight={150}
                    responsiveContainerWidth={100}
                    variant="large"
                />
                <Steps steps={steps} />
            </Flex>
            <Flex justifyContent="flex-end" marginTop="auto">
                <Button
                    data-trackid="customPanelShortcutLink_skipOnboarding"
                    variant="tertiary"
                    size="small"
                    onClick={skipOnboarding}
                >
                    {t('onboarding.skip')}
                </Button>
            </Flex>
        </Flex>
    );
};

export const Shortcuts = () => {
    const { context } = usePanelContext();

    const { clientId } = context.variables as { clientId: string };
    const storageKey = `isOnboarding:${clientId}`;

    const [isSkipped, setIsSkipped] = useState(
        localStorage.getItem(storageKey) === 'skipped'
    );

    const {
        queryResults: { data = {}, isFetching }
    } = usePanelContext();

    const { agentDeployed, idpIntegration, saasOpsIntegration } = data;

    // Set isOnboarding cookie if conditions are not met
    useEffect(() => {
        if (
            !isFetching &&
            (!agentDeployed?.completed ||
                !idpIntegration?.completed ||
                !saasOpsIntegration?.completed)
        ) {
            localStorage.setItem(storageKey, isSkipped ? 'skipped' : 'true');
        }
    }, [
        isFetching,
        agentDeployed,
        idpIntegration,
        saasOpsIntegration,
        isSkipped,
        storageKey
    ]);

    const shouldShowOnboarding =
        (!agentDeployed?.completed ||
            !idpIntegration?.completed ||
            !saasOpsIntegration?.completed) &&
        !isSkipped;

    return (
        <Flex sx={{ overflow: 'auto', padding: '0 24px' }}>
            {shouldShowOnboarding ? (
                <OnboardingShortcuts
                    skipOnboarding={() => {
                        localStorage.setItem(storageKey, 'skipped');
                        setIsSkipped(true);
                    }}
                />
            ) : (
                <NormalShortcuts />
            )}
        </Flex>
    );
};

/**
 * Skeleton for the Shortcuts panel.
 * This is used to show a loading state for the Shortcuts panel.
 */
export const ShortcutsSkeleton = () => (
    <Flex sx={{ padding: '0 24px' }}>
        <Flex
            flexDirection="column"
            sx={{ gap: '24px', width: '100%' }}
            flexGrow={1}
        >
            {/* Progress circle skeleton for onboarding mode */}
            <Flex
                sx={{ gap: '24px' }}
                alignItems="center"
                flexDirection="column"
            >
                <Skeleton
                    variant="circular"
                    width={150}
                    height={150}
                    sx={{ marginBottom: '8px' }}
                />
            </Flex>

            {/* Steps skeleton */}
            {Array.from({ length: 3 }).map((_, index) => (
                <Flex
                    // eslint-disable-next-line react/no-array-index-key
                    key={`skeleton-${index}`}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ gap: '8px' }}
                >
                    <Flex alignItems="center" sx={{ gap: '8px' }}>
                        <Skeleton variant="circular" width={16} height={16} />
                        <Skeleton variant="text" width={200} height={30} />
                    </Flex>
                    <Skeleton
                        variant="rectangular"
                        width={80}
                        height={32}
                        sx={{ borderRadius: '4px' }}
                    />
                </Flex>
            ))}

            {/* Skip button skeleton */}
            <Flex justifyContent="flex-end" marginTop="auto">
                <Skeleton
                    variant="rectangular"
                    width={120}
                    height={32}
                    sx={{ borderRadius: '4px' }}
                />
            </Flex>
        </Flex>
    </Flex>
);
